import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { PROJECTS } from "@/data/content";
import { useLang } from "@/i18n/LangContext";

const Portfolio = () => {
    const [filter, setFilter] = useState("all");
    const { t, lang } = useLang();
    const p = t.portfolio;

    useEffect(() => {
        document.title = lang === "sw" ? "Kazi Zetu — Hallomedics" : "Work — Hallomedics";
    }, [lang]);

    const visible = filter === "all" ? PROJECTS : PROJECTS.filter((pr) => pr.category === filter);

    return (
        <div className="pt-28 lg:pt-36" data-testid="portfolio-page">
            <header className="mx-auto max-w-7xl px-5 sm:px-8">
                <p className="eyebrow flex items-center gap-2.5" data-testid="portfolio-eyebrow">
                    <span className="inline-block h-2 w-2 rounded-full bg-teal animate-pulse-dot" />
                    {p.eyebrow}
                </p>
                <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl" data-testid="portfolio-heading" key={lang}>
                    <MaskedLine delay={0.1}>{p.h1}</MaskedLine>
                </h1>
                <Reveal delay={0.25}>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">{p.intro}</p>
                </Reveal>

                <Reveal delay={0.35}>
                    <div className="mt-10 flex flex-wrap gap-2.5" role="tablist" aria-label="Filter projects" data-testid="portfolio-filters">
                        {p.filters.map((f) => (
                            <button
                                key={f.key}
                                type="button"
                                role="tab"
                                aria-selected={filter === f.key}
                                data-testid={`filter-${f.key}`}
                                onClick={() => setFilter(f.key)}
                                className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-all duration-200 active:scale-[0.97] ${
                                    filter === f.key
                                        ? "border-primary bg-primary text-primary-foreground"
                                        : "border-border bg-card text-muted-foreground hover:border-primary hover:text-primary dark:hover:border-teal dark:hover:text-teal"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </Reveal>
            </header>

            <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20" data-testid="portfolio-grid">
                <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    <AnimatePresence mode="popLayout">
                        {visible.map((pr) => (
                            <motion.article
                                layout
                                key={pr.id}
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.96 }}
                                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                className="group overflow-hidden rounded-md border border-border bg-card transition-shadow duration-300 hover:shadow-[0_18px_40px_-18px_rgba(15,76,58,0.35)]"
                                data-testid={`project-card-${pr.id}`}
                            >
                                <div className="relative overflow-hidden">
                                    <img src={pr.image} alt={pr.title} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                                    <span className="absolute left-4 top-4 rounded-full bg-background/85 px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground backdrop-blur">
                                        {p.categoryLabels[pr.category]}
                                    </span>
                                </div>
                                <div className="p-6">
                                    <h2 className="font-heading text-xl font-semibold tracking-tight">{pr.title}</h2>
                                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                                        {lang === "sw" ? pr.descriptionSw : pr.description}
                                    </p>
                                    <p className="mt-4 border-t border-border pt-4 font-mono text-xs uppercase tracking-[0.14em] text-terracotta">
                                        {lang === "sw" ? pr.metricSw : pr.metric}
                                    </p>
                                </div>
                            </motion.article>
                        ))}
                    </AnimatePresence>
                </motion.div>

                <Reveal delay={0.1}>
                    <div className="mt-16 flex flex-col items-start justify-between gap-6 rounded-lg border border-border bg-secondary/50 p-8 sm:flex-row sm:items-center">
                        <div>
                            <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">{p.ctaH2}</h2>
                            <p className="mt-1 text-sm text-muted-foreground">{p.ctaCopy}</p>
                        </div>
                        <Link
                            to="/contact"
                            data-testid="portfolio-cta-button"
                            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                        >
                            {p.ctaButton}
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Reveal>
            </section>
        </div>
    );
};

export default Portfolio;
